import { useState, useRef, useEffect, useCallback } from "react";
import { Plus, Trash2, X, ImageIcon, ChevronUp, ChevronDown, ChevronRight, Info, Clock, HandCoins, Lock } from "lucide-react";
import { confirmDialog, showError, showSuccess, showWarning, notifyApiError } from "../../../components/alertas";
import { mostrarSpinner, ocultarSpinner } from "../../../components/Spinner";
import ClientService from "../../../services/client/ClientService";
import Sheet from "../../../components/sheet/Sheet";
import useMediaQuery from "../../../hooks/useMediaQuery";
import logger from "../../../utils/logger";
import "./servicios.css";

// Paleta de colores de Google Calendar
const GOOGLE_CALENDAR_COLORS = [
  { id: 1, name: 'Lavanda', hex: '#7986cb' },
  { id: 2, name: 'Salvia', hex: '#33b679' },
  { id: 3, name: 'Uva', hex: '#8e24aa' },
  { id: 4, name: 'Flamenco', hex: '#e67c73' },
  { id: 5, name: 'Plátano', hex: '#fbc02d' },
  { id: 6, name: 'Mandarina', hex: '#fc7e09' },
  { id: 7, name: 'Pavo Real', hex: '#039be5' },
  //{ id: 8, name: 'Grafito', hex: '#616161' }, desactivado ya que es el mismo color al de bloqueo de pendiente
  { id: 9, name: 'Arándano', hex: '#3f51b5' },
  { id: 10, name: 'Albahaca', hex: '#0b8043' },
  { id: 11, name: 'Tomate', hex: '#d50000' },
];
const colorHex = (id) => GOOGLE_CALENDAR_COLORS.find(c => c.id === id)?.hex;

const ADVANCE_PERCENT_OPTIONS = [10, 15, 20, 25, 30, 40, 50];
const BLOCK_OPTIONS = [10, 15, 20, 30, 45, 60];

const calculateAdvanceAmount = (price, percentage) => {
  const numericPrice = Number(price);
  const numericPercentage = Number(percentage);
  if (!Number.isFinite(numericPrice) || numericPrice <= 0 || !Number.isFinite(numericPercentage) || numericPercentage <= 0) return 0;
  return Math.ceil((numericPrice * numericPercentage) / 100);
};

const getAdvancePercentageFromService = (service) => {
  const price = Number(service?.price);
  const advanceAmount = Number(service?.advanceAmount);
  if (!Number.isFinite(price) || price <= 0 || !Number.isFinite(advanceAmount) || advanceAmount <= 0) return '';
  const exactMatch = ADVANCE_PERCENT_OPTIONS.find((percentage) => calculateAdvanceAmount(price, percentage) === advanceAmount);
  return exactMatch ? String(exactMatch) : '';
};

const EMPTY_SERVICE = { name: '', description: '', price: '', durationMinutes: '', images: [], requiresAdvance: false, advanceAmount: null, advancePercentage: '' };
const money = (n) => `$${(Number(n) || 0).toLocaleString("es-MX")}`;

/**
 * Servicios: bloque base como control segmentado (se bloquea con servicios creados),
 * lista ordenable con precio y duración a la vista; alta y edición en sheet. El
 * estado `services` vive en el panel (lo comparten Personalización y el guardado).
 */
function ServicesTab({ clientData, preloadedImages, onConfigComplete, handleUpdateClient, brandSettings, blockBase, setBlockBase, services, setServices }) {
  const isDesktop = useMediaQuery("(min-width: 992px)");
  const side = isDesktop ? "right" : "bottom";
  const [editedMap, setEditedMap] = useState({});
  const [showNew, setShowNew] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [showBlockInfo, setShowBlockInfo] = useState(false);
  const [newService, setNewService] = useState(EMPTY_SERVICE);
  const [selectedFiles, setSelectedFiles] = useState({});
  const fileInputsRef = useRef({});
  const [hasAdvanceInstructions, setHasAdvanceInstructions] = useState(false);

  // Sin instrucciones de anticipo (Bot) no puede haber servicios con anticipo: se limpian y guardan.
  const verifyAdvanceInstructions = useCallback(() => {
    if (!clientData) return;
    const current = !!(clientData?.paymentSettings?.advanceInstructions && clientData.paymentSettings.advanceInstructions.trim() !== '');
    setHasAdvanceInstructions(current);
    if (!current && services.some(s => s.requiresAdvance)) {
      const cleanedServices = services.map(s => ({ ...s, requiresAdvance: false, advanceAmount: null }));
      setServices(cleanedServices);
      handleUpdateClient({ ...brandSettings, business: { ...clientData.business, services: cleanedServices, blockBase } }, "Servicios (Auto-limpieza)", {});
    }
  }, [clientData, services, brandSettings, blockBase, handleUpdateClient, setServices]);

  useEffect(() => { verifyAdvanceInstructions(); }, [verifyAdvanceInstructions]);

  const handleFileSelect = (serviceIndex, event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(prev => ({ ...prev, [serviceIndex]: [...(prev[serviceIndex] || []), ...files].slice(0, 4) }));
  };

  const openFileDialog = (serviceIndex) => {
    if (!fileInputsRef.current[serviceIndex]) {
      const input = document.createElement('input');
      input.type = 'file'; input.multiple = true; input.accept = 'image/*';
      input.onchange = (e) => handleFileSelect(serviceIndex, e);
      fileInputsRef.current[serviceIndex] = input;
    }
    fileInputsRef.current[serviceIndex].click();
  };

  const removeSelectedFile = async (serviceIndex, combinedIndex) => {
    const service = services[serviceIndex] || { images: [] };
    const existingCount = (service.images || []).length;
    if (combinedIndex < existingCount) {
      // Imagen ya en el servidor: quitar del arreglo y persistir
      const updatedServices = services.map((s, i) => i !== serviceIndex ? s : { ...s, images: (s.images || []).filter((_, idx) => idx !== combinedIndex) });
      setServices(updatedServices);
      try {
        await handleUpdateClient({ business: { ...clientData.business, services: updatedServices } }, "Servicios", selectedFiles);
        showSuccess("Imagen eliminada", "Imagen removida del servicio.");
      } catch (err) {
        logger.error("Error eliminando imagen existente:", err);
        showError("Error", "No se pudo eliminar la imagen. Intenta de nuevo.");
      }
    } else {
      const selectedIndex = combinedIndex - existingCount;
      setSelectedFiles(prev => ({ ...prev, [serviceIndex]: (prev[serviceIndex] || []).filter((_, i) => i !== selectedIndex) }));
    }
  };

  const handleAddService = () => {
    if (!newService.durationMinutes || newService.durationMinutes % blockBase !== 0) {
      showError("Duración inválida", `La duración debe ser múltiplo de ${blockBase} minutos (tu bloque base).`);
      return;
    }
    if (!hasAdvanceInstructions && newService.requiresAdvance) {
      showError("No hay instrucciones de anticipo", "Escribe primero cómo pagar el anticipo en la sección Bot de WhatsApp.");
      return;
    }
    const { advancePercentage, ...serviceBase } = newService;
    const serviceToCreate = { ...serviceBase, advanceAmount: newService.requiresAdvance ? calculateAdvanceAmount(newService.price, advancePercentage || 20) : null };
    const updatedServices = [...services, serviceToCreate];
    handleUpdateClient({ ...brandSettings, business: { ...clientData.business, services: updatedServices, blockBase } }, "Servicios", selectedFiles).then(() => onConfigComplete());
    setServices(updatedServices);
    setShowNew(false);
    setNewService(EMPTY_SERVICE);
  };

  const handleUpdateService = (index, updatedService) => {
    const { advancePercentage, ...serviceBase } = updatedService;
    setServices(services.map((service, i) => i === index ? { ...service, ...serviceBase } : service));
    setEditedMap(prev => ({ ...prev, [index]: true }));
  };

  const saveService = (index) => {
    const service = services[index];
    if (Number(service.durationMinutes) % blockBase !== 0) {
      showError("Duración inválida", `La duración debe ser múltiplo de ${blockBase} minutos.`);
      return false;
    }
    let updatedService = { ...service };
    if (!hasAdvanceInstructions && service.requiresAdvance) updatedService = { ...service, requiresAdvance: false, advanceAmount: null };
    const { advancePercentage, ...servicePayload } = updatedService;
    if (servicePayload.requiresAdvance && !servicePayload.advanceAmount) {
      showError("Monto de anticipo requerido", "Si el servicio requiere anticipo, elige el porcentaje.");
      return false;
    }
    if (!editedMap[index] && !(selectedFiles[index] && selectedFiles[index].length > 0)) {
      showSuccess("Sin cambios", "No hay cambios para guardar en este servicio.");
      return true;
    }
    const updatedServices = services.map((s, i) => i === index ? servicePayload : s);
    handleUpdateClient({ ...brandSettings, business: { ...clientData.business, services: updatedServices }, blockBase }, "Servicios", selectedFiles);
    setEditedMap(prev => ({ ...prev, [index]: false }));
    return true;
  };

  const moveService = async (index, direction) => {
    if (index + direction < 0 || index + direction >= services.length) return;
    const newServices = [...services];
    [newServices[index], newServices[index + direction]] = [newServices[index + direction], newServices[index]];
    setServices(newServices);
    try {
      mostrarSpinner();
      await ClientService.reorderServices(clientData.id, newServices.map((s, i) => ({ ...s, order: i })));
      showSuccess("Orden actualizado", "Los servicios fueron reordenados.");
    } catch (error) {
      notifyApiError(error, "Reordenar servicios");
    } finally {
      ocultarSpinner();
    }
  };

  const deleteService = async (index) => {
    const ok = await confirmDialog({ title: `¿Eliminar "${services[index]?.name}"?`, text: "Las citas ya agendadas no cambian, pero tus clientes ya no podrán elegirlo.", confirmButtonText: "Sí, eliminar", cancelButtonText: "Cancelar", icon: "warning", danger: true });
    if (!ok) return;
    try {
      mostrarSpinner();
      const updatedServices = services.filter((_, i) => i !== index);
      setServices(updatedServices);
      setEditingIndex(null);
      await handleUpdateClient({ ...brandSettings, business: { ...clientData.business, services: updatedServices, blockBase } }, "Servicios", {});
      showSuccess("Servicio eliminado", "El servicio ha sido eliminado correctamente.");
    } catch (error) {
      logger.error("Error eliminando servicio:", error);
      showError("Error", "No se pudo eliminar el servicio. Intenta de nuevo.");
    } finally {
      ocultarSpinner();
    }
  };

  const openNew = () => {
    if (!blockBase) {
      showWarning("Elige el bloque base primero", "Antes de agregar servicios, define cada cuántos minutos se ofrecen horarios.");
      return;
    }
    setShowNew(true);
  };

  const blockLocked = services.length > 0;
  const blockOptions = Number(blockBase) && !BLOCK_OPTIONS.includes(Number(blockBase)) ? [...BLOCK_OPTIONS, Number(blockBase)].sort((a, b) => a - b) : BLOCK_OPTIONS;
  const editing = editingIndex != null ? services[editingIndex] : null;

  return (
    <section className="cp-services">
      <header className="cp-page-head">
        <div>
          <h2 className="cp-page-title" data-tour="servicios.title">Servicios {services.length > 0 && <span className="cp-count">{services.length}</span>}</h2>
        </div>
        <div className="cp-page-actions">
          <button type="button" className="cp-btn cp-btn-primary" data-tour="servicios.add" onClick={openNew}>
            <Plus size={18} /> <span>Nuevo servicio</span>
          </button>
        </div>
      </header>

      <article className="cp-card cp-svc-block" data-tour="servicios.block">
        <div className="cp-card-head">
          <h3 className="cp-card-title"><Clock size={18} /> Bloque base</h3>
          <button type="button" className="cp-linkbtn" onClick={() => setShowBlockInfo(true)}><Info size={15} /> ¿Qué es?</button>
        </div>
        <p className="cp-muted">Cada cuántos minutos se ofrecen horarios. Las duraciones de tus servicios deben ser múltiplos de este valor.</p>
        {blockLocked ? (
          <div className="cp-svc-blockvalue">
            <span className="cp-svc-blockvalue-num">{blockBase} min</span>
            <span className="cp-hint cp-svc-lock"><Lock size={12} /> Con servicios creados ya no se puede cambiar.</span>
          </div>
        ) : (
          <div className="cp-segment-scroll">
            <div className="cp-segment" role="radiogroup" aria-label="Bloque base en minutos">
              {blockOptions.map(min => (
                <button key={min} type="button" role="radio" aria-checked={Number(blockBase) === min} className={`cp-segment-btn ${Number(blockBase) === min ? "is-active" : ""}`} onClick={() => setBlockBase(min)}>
                  {min} min
                </button>
              ))}
            </div>
          </div>
        )}
      </article>

      {services.length === 0 ? (
        <div className="cp-card">
          <div className="cp-empty">
            <ImageIcon size={36} />
            <h3>Aún no tienes servicios</h3>
            <p>Nombre, precio y duración es lo mínimo. Es lo que tus clientes eligen al agendar.</p>
            <button type="button" className="cp-btn cp-btn-primary" onClick={openNew}><Plus size={16} /> Nuevo servicio</button>
          </div>
        </div>
      ) : (
        <ul className="cp-list cp-svc-list">
          {services.map((service, index) => (
            <li key={service.id || index} className="cp-svc-item" data-tour={index === 0 ? "servicios.list" : undefined}>
              <div className="cp-svc-order">
                <button type="button" className="cp-iconbtn" onClick={() => moveService(index, -1)} disabled={index === 0} aria-label="Mover arriba"><ChevronUp size={16} /></button>
                <button type="button" className="cp-iconbtn" onClick={() => moveService(index, 1)} disabled={index === services.length - 1} aria-label="Mover abajo"><ChevronDown size={16} /></button>
              </div>
              <button type="button" className="cp-row" onClick={() => setEditingIndex(index)}>
                <span className="cp-svc-thumb" style={{ background: colorHex(service.colorId) ? `${colorHex(service.colorId)}22` : undefined }}>
                  {service.images?.[0] && preloadedImages?.[index]?.[0]
                    ? <img src={preloadedImages[index][0]} alt="" />
                    : <span className="cp-dot" style={{ background: colorHex(service.colorId) || "var(--cp-text-3)" }} />}
                </span>
                <span className="cp-row-main">
                  <span className="cp-row-title">{service.name || <span className="cp-muted">Sin nombre</span>}</span>
                  <span className="cp-row-sub">
                    <span>{service.durationMinutes} min</span>
                    <span aria-hidden>·</span>
                    <span>{money(service.price)}</span>
                    {service.requiresAdvance && <span className="cp-pill cp-pill-warn"><HandCoins size={11} /> {money(service.advanceAmount)}</span>}
                    {editedMap[index] && <span className="cp-pill cp-pill-accent">Sin guardar</span>}
                  </span>
                </span>
                <ChevronRight size={18} className="cp-chev" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Sheet: nuevo servicio */}
      <Sheet open={showNew} onClose={() => { setShowNew(false); setNewService(EMPTY_SERVICE); }} side={side} title="Nuevo servicio" subtitle={`Duración en múltiplos de ${blockBase} min`} className="cp-vars"
        footer={
          <div className="cp-sheet-actions">
            <button type="button" className="cp-btn" onClick={() => { setShowNew(false); setNewService(EMPTY_SERVICE); }}>Cancelar</button>
            <button type="button" className="cp-btn cp-btn-primary" onClick={handleAddService} disabled={!newService.name.trim() || !String(newService.price).trim() || !String(newService.durationMinutes).trim()}>
              <Plus size={16} /> Agregar
            </button>
          </div>
        }
      >
        <ServiceForm service={newService} onChange={setNewService} blockBase={blockBase} hasAdvanceInstructions={hasAdvanceInstructions} isNew />
      </Sheet>

      {/* Sheet: editar servicio */}
      <Sheet open={editing != null} onClose={() => setEditingIndex(null)} side={side} title={editing?.name || "Servicio"} subtitle="Editar servicio" className="cp-vars"
        footer={editing && (
          <div className="cp-sheet-actions">
            <button type="button" className="cp-btn cp-btn-danger" onClick={() => deleteService(editingIndex)}><Trash2 size={16} /> Eliminar</button>
            <button type="button" className="cp-btn cp-btn-primary" onClick={() => { if (saveService(editingIndex)) setEditingIndex(null); }}>Guardar</button>
          </div>
        )}
      >
        {editing && (
          <>
            <ServiceForm service={editing} onChange={(s) => handleUpdateService(editingIndex, s)} blockBase={blockBase} hasAdvanceInstructions={hasAdvanceInstructions} />
            <ServiceImages
              service={editing}
              preloaded={preloadedImages?.[editingIndex] || []}
              files={selectedFiles[editingIndex] || []}
              onAdd={() => openFileDialog(editingIndex)}
              onRemove={(i) => removeSelectedFile(editingIndex, i)}
            />
          </>
        )}
      </Sheet>

      {/* Sheet: qué es el bloque base */}
      <Sheet open={showBlockInfo} onClose={() => setShowBlockInfo(false)} side={side} title="El bloque base" subtitle="La unidad de tiempo de tu agenda" className="cp-vars"
        footer={<div className="cp-sheet-actions is-single"><button type="button" className="cp-btn cp-btn-primary" onClick={() => setShowBlockInfo(false)}>Entendido</button></div>}
      >
        <div className="cp-svc-info">
          <p>Es cada cuántos minutos se ofrecen horarios. Con <b>30</b>, tus clientes ven 10:00, 10:30, 11:00… y cada servicio dura 30, 60 o 90 minutos.</p>
          <p>Elige el que se ajuste a tu servicio más corto. <b>15 minutos</b> da más flexibilidad: permite servicios de 15, 30, 45, 60, 75 o 90.</p>
          <p className="cp-muted">Una vez que agregues servicios ya no se puede cambiar, porque todos dependen de él.</p>
        </div>
      </Sheet>
    </section>
  );
}

/** Campos de un servicio (alta y edición). `service` puede traer advancePercentage solo en alta. */
function ServiceForm({ service, onChange, blockBase, hasAdvanceInstructions, isNew }) {
  const percentage = isNew ? service.advancePercentage : getAdvancePercentageFromService(service);
  const previewPct = Number(percentage || 20);
  const previewAmount = calculateAdvanceAmount(service.price, previewPct);
  const durationOk = !service.durationMinutes || Number(service.durationMinutes) % blockBase === 0;

  const setAdvance = (checked) => onChange({
    ...service,
    requiresAdvance: checked,
    advanceAmount: checked ? calculateAdvanceAmount(service.price, percentage || 20) : null,
  });

  return (
    <div className="cp-form cp-svc-form">
      <label className="cp-field">
        <span>Nombre</span>
        <input type="text" className="cp-input" placeholder="Corte de cabello" value={service.name ?? ''} onChange={(e) => onChange({ ...service, name: e.target.value })} autoComplete="off" />
      </label>
      <label className="cp-field">
        <span>Descripción <small>(opcional)</small></span>
        <textarea className="cp-input" rows={2} placeholder="Corte clásico para hombres" value={service.description ?? ''} onChange={(e) => onChange({ ...service, description: e.target.value })} />
      </label>
      <div className="cp-grid-2">
        <label className="cp-field">
          <span>Precio ($)</span>
          <input type="number" inputMode="decimal" min={0} className="cp-input" placeholder="150" value={service.price ?? ''} onChange={(e) => {
            const nextPrice = e.target.value;
            onChange({ ...service, price: nextPrice, ...(service.requiresAdvance ? { advanceAmount: calculateAdvanceAmount(nextPrice, percentage || 20) } : {}) });
          }} />
        </label>
        <label className="cp-field">
          <span>Duración (min)</span>
          <input type="number" inputMode="numeric" min={blockBase} step={blockBase} className="cp-input" placeholder={String(blockBase)} value={service.durationMinutes ?? ''} onChange={(e) => onChange({ ...service, durationMinutes: e.target.value })} />
          <small className={durationOk ? "" : "cp-svc-err"}>{durationOk ? `Múltiplo de ${blockBase}` : `Debe ser múltiplo de ${blockBase}`}</small>
        </label>
      </div>

      <div className="cp-field">
        <span>Color en el calendario</span>
        <div className="cp-swatches">
          {GOOGLE_CALENDAR_COLORS.map(color => (
            <button key={color.id} type="button" className={`cp-swatch ${service.colorId === color.id ? "is-active" : ""}`} style={{ background: color.hex }} onClick={() => onChange({ ...service, colorId: color.id })} title={color.name} aria-label={color.name} aria-pressed={service.colorId === color.id} />
          ))}
        </div>
      </div>

      {hasAdvanceInstructions ? (
        <>
          <div className="cp-switch">
            <div>
              <div className="cp-switch-label">Pedir anticipo</div>
              <div className="cp-switch-hint">La cita queda pendiente hasta que confirmes el comprobante.</div>
            </div>
            <button type="button" role="switch" aria-checked={!!service.requiresAdvance} className="cp-toggle" onClick={() => setAdvance(!service.requiresAdvance)} aria-label="Pedir anticipo" />
          </div>
          {service.requiresAdvance && (
            <label className="cp-field">
              <span>Porcentaje del anticipo</span>
              <select className="cp-input" value={percentage} disabled={!Number(service.price) || Number(service.price) <= 0} onChange={(e) => {
                const pct = Number(e.target.value);
                onChange({ ...service, ...(isNew ? { advancePercentage: e.target.value } : {}), advanceAmount: Number.isFinite(pct) && pct > 0 ? calculateAdvanceAmount(service.price, pct) : null });
              }}>
                <option value="">Elige un porcentaje</option>
                {ADVANCE_PERCENT_OPTIONS.map(p => <option key={p} value={p}>{p}%</option>)}
              </select>
              <small>{Number(service.price) > 0 ? `${previewPct}% de ${money(service.price)} = ${money(previewAmount)}` : "Pon primero el precio para calcularlo."}</small>
            </label>
          )}
        </>
      ) : (
        <div className="cp-note"><Info size={16} /><div><b>Para pedir anticipos</b> escribe primero cómo pagarlo en <b>Bot de WhatsApp</b>. Ahí el bot lo manda junto con la solicitud de cita.</div></div>
      )}
      {isNew && <p className="cp-hint">Las fotos se agregan después de crear el servicio.</p>}
    </div>
  );
}

/** Fotos del servicio: hasta 4 entre existentes (URLs precargadas) y recién elegidas (File). */
function ServiceImages({ service, preloaded, files, onAdd, onRemove }) {
  const existing = service.images || [];
  const all = [...existing, ...files].slice(0, 4);
  return (
    <div className="cp-field cp-svc-images">
      <span>Fotos <small>(hasta 4; la primera es la principal)</small></span>
      <div className="cp-svc-gallery">
        {all.map((image, i) => (
          <div key={i} className={`cp-svc-photo ${i === 0 ? "is-main" : ""}`}>
            {typeof image === 'string'
              ? (preloaded[i] ? <img src={preloaded[i]} alt={`Foto ${i + 1}`} /> : <span className="cp-spin" />)
              : <img src={URL.createObjectURL(image)} alt={`Foto ${i + 1}`} />}
            <button type="button" className="cp-svc-photo-x" onClick={() => onRemove(i)} aria-label="Quitar foto"><X size={14} /></button>
          </div>
        ))}
        {all.length < 4 && (
          <button type="button" className="cp-svc-photo cp-svc-photo-add" onClick={onAdd}>
            <ImageIcon size={20} /><span>Agregar</span>
          </button>
        )}
      </div>
    </div>
  );
}

export default ServicesTab;
